// A PAGE THE BROWSER HANDED BACK FROM BEFORE A LENS SWITCH — and, since the
// lens went into the URL, what to do about it.
//
// ⚠️ INVERTED SINCE THE LENS WENT INTO THE URL. Read the
// history below as the mechanism, not as the rule: the rule used to be "a page
// drawn from an older cookie is stale, reload it under the new one", and it is
// now "a page whose URL names its lens is RIGHT for that history entry, and the
// cookie follows it". Only a document with no `?as=` — one drawn before the lens
// was in the URL — still gets the reload.
//
// THE MECHANISM, measured in Chromium (2026-09-16). An admin switches lens,
// goes on to another document, and presses Back. The browser can hand back the
// document as it was served BEFORE the switch. `next dev` answers every page
// `no-cache, must-revalidate` ON PURPOSE ("so the browser can restore them from
// the HTTP cache on back/forward", base-server.js), and a history traversal
// takes a `no-cache` document from the disk cache without asking. The restored
// HTML is the one served before the switch, and the page hydrates as the lens it
// was drawn with: `fromDiskCache: true`, `pageshow.persisted: false`,
// navigation type `back_forward`, and no request reaches the server. A Back that
// stays inside one document is NOT this: that belongs to the client router.
//
// Production answers a dynamic page `private, no-cache, no-store` (Next's
// cache-control.js), so that disk-cache path is dev's alone. The same old
// document can still come back from the back/forward cache, as a frozen copy
// of the page you left (`pageshow.persisted: true`). Browsers disagree on
// caching `no-store` pages there, and Playwright's Chromium runs with the cache
// off, so that path is covered by construction, not by a browser.
//
// WHY THE LENS IN THE URL TURNS THIS AROUND. Every admin history entry names
// its lens (`?as=`, stamped by components/lens-url-sync.tsx, L2), and the page
// it restores was drawn through exactly that lens — the server reads `?as=`
// before the cookie. So the restored page is not stale: it is the entry the
// admin went back to, showing what it showed. Back to Admin is Admin, which is
// the whole of what was asked for. What IS stale then is the COOKIE, which still
// holds the lens switched to since, and which the next link without `?as=` (the
// sidebar) would render through. So the cookie is re-written to this entry's
// lens, and nothing is reloaded (`onLensShown`).
//
// THE RELOAD SURVIVES FOR A DOCUMENT WITH NO `?as=`, which can only be one drawn
// before this shipped (or one whose script never ran to stamp it). That page
// says nothing about its lens except the cookie it was drawn from, so it is
// judged as before, by comparing cookies (`reloadIfLensStale`):
//
// ⚠️ IT COMPARES THE COOKIE, NOT THE LENS. The server renders from the cookie as
// it saw it, and passes that string down (app/layout.tsx). A page whose cookie
// has moved on since it was rendered is stale, whatever the lens inside it
// says. Comparing the raw value rather than the parsed persona is what makes it
// loop-proof: after the reload the server sees the cookie this check just read,
// so the two agree by construction. A forged or outdated cookie that the server
// refuses to honour still reads back as the same string, so it can't loop either.
// That only holds if the client reads the cookie EXACTLY as Next does, which is
// what `personaCookieValue` is for, and what its test pins against Next's own
// parser.

import { ADMIN_LENS_KEY, PERSONA_COOKIE, PERSONA_COOKIE_MAX_AGE, lensCookieValue, lensFromCookie } from "@/lib/persona-cookie";
import { LENS_PARAM } from "@/lib/lens-url";
import type { SwitchableLens } from "@/lib/persona-types";

/**
 * The persona cookie out of a `Cookie` header or `document.cookie`, read the way
 * Next's `cookies().get()` reads it (@edge-runtime/cookies' parseCookie): split
 * on `;`, value decoded, a pair that won't decode skipped, a bare name read as
 * "true", and the LAST of two same-named pairs winning. null when absent.
 */
export function personaCookieValue(cookieHeader: string): string | null {
  let found: string | null = null;
  for (const pair of cookieHeader.split(/; */)) {
    const at = pair.indexOf("=");
    if (at === -1) {
      if (pair === PERSONA_COOKIE) found = "true"; // Next's reading of a bare name
      continue;
    }
    if (pair.slice(0, at) !== PERSONA_COOKIE) continue;
    try {
      found = decodeURIComponent(pair.slice(at + 1));
    } catch {
      // Next drops a value it can't decode; so does this.
    }
  }
  return found;
}

/**
 * If this page was rendered under a persona cookie other than the one the
 * browser holds now, load it again. Returns true when a reload was started.
 *
 * `location.replace(href)`, as in lib/stale-build.ts: a plain GET of the same
 * URL that stands in for the restored entry rather than adding one.
 */
export function reloadIfLensStale(deps: {
  /** The cookie value the server rendered this page from (app/layout.tsx). */
  rendered: string | null;
  cookieHeader: string;
  location: { href: string; replace: (url: string) => void };
}): boolean {
  if (personaCookieValue(deps.cookieHeader) === deps.rendered) return false;
  deps.location.replace(deps.location.href);
  return true;
}

/**
 * Hand the lens's KEY to the server, for every request that carries no `?as=`
 * (lib/persona-cookie.ts; the server looks the key up among the lenses it
 * offers).
 *
 * Not httpOnly, and it doesn't need to be: it holds a view preference, not a
 * credential — nothing here grants anything the session hasn't already granted.
 * Lax so a link into the app still arrives in the view you left; Secure once
 * we're on https, which local dev over http is not.
 */
export function writeLensCookie(
  key: string,
  env: { document: { cookie: string }; protocol: string } = { document, protocol: window.location.protocol },
): void {
  const secure = env.protocol === "https:" ? "; secure" : "";
  env.document.cookie =
    `${PERSONA_COOKIE}=${lensCookieValue(key)}; path=/; max-age=${PERSONA_COOKIE_MAX_AGE}; samesite=lax${secure}`;
}

/**
 * THE LENS THE COOKIE NAMES, as the server would resolve it: the key of a lens
 * the roster offers, or the identity's own for anything else (absent, forged, a
 * person since deleted) — the roster's first lens, which is always the
 * identity's (`admin` for the console, a staff seat's own slug). What an address
 * with no `?as=` was drawn through, so what it is stamped with.
 */
export function cookieLensKey(cookieHeader: string, switchable: readonly SwitchableLens[]): string {
  const p = lensFromCookie(personaCookieValue(cookieHeader), switchable);
  return (p && switchable.find((l) => l.persona === p)?.key) || switchable[0]?.key || ADMIN_LENS_KEY;
}

/**
 * A PAGE SHOWN AGAIN — hydrated from a cached document, or thawed from the
 * back/forward cache. What it needs depends on whether its URL names its lens:
 *
 *   `?as=` in the URL   the page is right for this entry. The cookie follows it,
 *                       so the next link without `?as=` opens in the same lens.
 *                       No reload. → "followed" (or "none" if it already agreed)
 *   no `?as=`           a page from before the lens was in the URL: reload it if
 *                       the cookie moved on since it was drawn. → "reloaded"
 *
 * The lens followed is the one the URL names, resolved against the roster — or,
 * for a `?as=` naming nothing, `current`, the lens the page fell back to and is
 * showing. Never the raw param, so a hand-typed key naming nothing is not
 * written into the cookie.
 */
export function onLensShown(deps: {
  search: string;
  /** The key of the lens on screen, or null when there is none to name. */
  current: string | null;
  /** The lenses the server offered this document. */
  switchable: readonly SwitchableLens[];
  /** The cookie value the server rendered this document from (app/layout.tsx). */
  rendered: string | null;
  cookieHeader: string;
  location: { href: string; replace: (url: string) => void };
  writeCookie: (key: string) => void;
}): "followed" | "reloaded" | "none" {
  const as = new URLSearchParams(deps.search).get(LENS_PARAM);
  if (as !== null) {
    const lens = deps.switchable.some((l) => l.key === as) ? as : deps.current;
    if (!lens || personaCookieValue(deps.cookieHeader) === lens) return "none";
    deps.writeCookie(lens);
    return "followed";
  }
  return reloadIfLensStale(deps) ? "reloaded" : "none";
}
